import { FunctionComponent, useCallback, useState } from "react";
import { Form } from "@ui-kit/Form/Form";
import { Checkbox } from "@ui-kit/Checkbox";
import { useAppDispatch, useAppSelector } from "@store/store";
import { selectClient } from "@store/client/clientReducer";
import { translate } from "@lib/translate";
import { registerUserAction } from "@store/auth/actions/registerUserAction";
import { selectEmail, setAuthViewAction } from "@store/sessionReducer";
import { useTranslation } from "react-i18next";
import { EmailField } from "../../../../components/formFields/EmailField/EmailField";
import { AuthLinkButton } from "../../components/AuthLinkButton/AuthLinkButton";
import { NameField } from "../../../../components/formFields/NameField/NameField";
import { FormStatus } from "../../../../components/formFields/FormStatus";
import { AuthSubmitButton } from "../../components/AuthSubmitButton/AuthSubmitButton";
import { AuthInstructions } from "../../components/AuthInstructions/AuthInstructions";
import { PostalCodeField } from "../../../../components/formFields/PostalCodeField/PostalCodeField";
import { AuthError } from "../../components/AuthError/AuthError";

interface RegisterProps {}

interface NewsletterValue {
  id: number;
  label: string;
  subscribed: boolean;
}

const getInitialValues = (
  newsletters: NewsletterValue[],
  email: string,
): {
  name: string;
  email: string;
  [key: string]: boolean | string;
} => {
  let value = { name: "", email: email };
  newsletters.forEach((newsletter) => {
    value[`newsletter_${newsletter.id}`] = newsletter.subscribed;
  });
  return value;
};

const Register: FunctionComponent<RegisterProps> = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [state, setState] = useState<{ status: FormStatus }>({
    status: "idle",
  });
  const client = useAppSelector(selectClient);
  const dispatch = useAppDispatch();
  const initialEmail = useAppSelector(selectEmail) ?? "";

  const newsletters = client.newsletters.map((newsletter) => {
    return {
      id: newsletter.id,
      label: newsletter.label,
      subscribed:
        client?.userAuthenticationFeature?.newsletterDefaultValue ?? true,
    };
  }) as NewsletterValue[];

  const handleSubmit = useCallback(
    async (values: {
      name: string;
      email: string;
      postalCode?: string;
      newsletter_: { [key: string]: boolean };
    }) => {
      setState({ status: "fetching" });

      const userNewsletters = client.newsletters
        .filter((x) => {
          return values[`newsletter_${x.id}`] === true;
        })
        .map((x) => {
          return { id: x.id };
        });

      try {
        await dispatch(
          registerUserAction({
            email: values.email,
            captcha: "",
            name: values.name,
            newsletters: userNewsletters,
            postalCode: values.postalCode,
          }),
        );
        setState({ status: "success" });
      } catch (_error) {
        setState({ status: "error" });
      }
    },
    [dispatch, client.newsletters],
  );

  const handleGoBack = useCallback(() => {
    dispatch(setAuthViewAction({ authView: "signInEmail" }));
  }, [dispatch]);

  const disabled = state.status === "fetching" || state.status === "success";

  return (
    <>
      <AuthInstructions />
      <div className="form-container">
        <Form
          form={form}
          name="register"
          layout="vertical"
          onFinish={handleSubmit}
          disabled={disabled}
          initialValues={getInitialValues(newsletters, initialEmail) as any}>
          <EmailField autoComplete="email" disabled={true} />
          <NameField disabled={disabled} />
          {client.userAuthenticationFeature?.requirePostalCode && (
            <PostalCodeField disabled={disabled} />
          )}
          <div className="newsletter-container">
            {newsletters.map((newsletter) => (
              <Form.Item
                name={`newsletter_${newsletter.id}`}
                key={newsletter.id}
                valuePropName="checked">
                <Checkbox name={`newsletter_${newsletter.id}`} form={form}>
                  {newsletter.label ??
                    translate("authentication.subscribeToNewsletterLabel")}
                </Checkbox>
              </Form.Item>
            ))}
          </div>
          <div className="form-button-container">
            <AuthSubmitButton
              label={t("common.continue")}
              disabled={disabled}
            />
            <AuthLinkButton
              label={t("common.goBack")}
              onPress={handleGoBack}
              disabled={disabled}
            />
          </div>
        </Form>
      </div>

      {state.status === "error" && <AuthError />}
    </>
  );
};

export { Register };
